// Player session management for Fokatmain games

const BACKEND_URL = window.location.origin;

class PlayerManager {
  constructor() {
    this.currentPlayer = null;
    this.loadPlayerFromStorage();
  }

  // Load player data from localStorage
  loadPlayerFromStorage() {
    try {
      const stored = localStorage.getItem('fokatmain_player');
      if (stored) {
        this.currentPlayer = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load player data:', error);
    }
  }

  // Save player data to localStorage
  savePlayerToStorage(player) {
    try {
      localStorage.setItem('fokatmain_player', JSON.stringify(player));
      this.currentPlayer = player;
    } catch (error) {
      console.error('Failed to save player data:', error);
    }
  }

  // Generate unique player ID
  generatePlayerId() {
    return 'player_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Get game attempts from localStorage
  getGameAttempts(gameId) {
    try {
      const attemptsKey = `fokatmain_attempts_${gameId}`;
      const stored = localStorage.getItem(attemptsKey);
      if (stored) {
        const data = JSON.parse(stored);
        // Check if attempts are still valid (not expired)
        const now = Date.now();
        if (data.expiresAt && now < data.expiresAt) {
          return data.attempts;
        }
      }
      return 0;
    } catch (error) {
      console.error('Failed to get game attempts:', error);
      return 0;
    }
  }

  // Set game attempts (after payment)
  setGameAttempts(gameId, attempts = 3) {
    try {
      const attemptsKey = `fokatmain_attempts_${gameId}`;
      const expiresAt = Date.now() + (24 * 60 * 60 * 1000); // Expires in 24 hours
      const data = {
        attempts,
        expiresAt,
        lastUpdated: Date.now()
      };
      localStorage.setItem(attemptsKey, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to set game attempts:', error);
    }
  }

  // Use one attempt
  useGameAttempt(gameId) {
    try {
      const currentAttempts = this.getGameAttempts(gameId);
      if (currentAttempts > 0) {
        this.setGameAttempts(gameId, currentAttempts - 1);
        return currentAttempts - 1; // Return remaining attempts
      }
      return 0;
    } catch (error) {
      console.error('Failed to use game attempt:', error);
      return 0;
    }
  }

  // Check if player has attempts for a game
  hasAttemptsForGame(gameId) {
    return this.getGameAttempts(gameId) > 0;
  }

  // Get or create anonymous player
  async getOrCreatePlayer(name = null, city = null) {
    if (this.currentPlayer) {
      return this.currentPlayer;
    }

    // Generate anonymous player data
    const playerId = this.generatePlayerId();
    const playerName = name || `Player${Math.floor(Math.random() * 9000) + 1000}`;
    const playerCity = city || this.getRandomCity();

    const playerData = {
      id: playerId,
      name: playerName,
      city: playerCity,
      created_at: new Date().toISOString(),
      games_played: 0,
      total_score: 0,
      best_score: 0
    };

    // Try to register with backend
    try {
      const response = await fetch(`${BACKEND_URL}/api/players`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: playerName,
          city: playerCity
        })
      });

      if (response.ok) {
        const serverPlayer = await response.json();
        playerData.id = serverPlayer.id;
        playerData.masked_name = serverPlayer.masked_name;
      }
    } catch (error) {
      console.error('Failed to register player with backend:', error);
      // Continue with local player data
    }

    this.savePlayerToStorage(playerData);
    return playerData;
  }

  // Submit game score
  async submitScore(gameId, gameName, score, gameData = {}) {
    const player = await this.getOrCreatePlayer();
    
    // Add anti-bot metadata
    const securityData = this.generateSecurityMetadata();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player_id: player.id,
          game_id: gameId,
          game_name: gameName,
          score: score,
          accuracy: gameData.accuracy,
          completion_time: gameData.completion_time,
          game_data: { ...gameData, ...securityData }
        })
      });

      if (response.ok) {
        // Update local player stats
        player.games_played = (player.games_played || 0) + 1;
        player.total_score = (player.total_score || 0) + score;
        player.best_score = Math.max(player.best_score || 0, score);
        this.savePlayerToStorage(player);
        
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to submit score:', error);
    }

    return null;
  }

  // Get player statistics
  getPlayerStats() {
    return this.currentPlayer ? {
      games_played: this.currentPlayer.games_played || 0,
      total_score: this.currentPlayer.total_score || 0,
      best_score: this.currentPlayer.best_score || 0,
      average_score: this.currentPlayer.games_played > 0 
        ? Math.round((this.currentPlayer.total_score || 0) / this.currentPlayer.games_played)
        : 0
    } : null;
  }

  // Get current player
  getCurrentPlayer() {
    return this.currentPlayer;
  }

  // Update player info
  async updatePlayer(name, city) {
    if (!this.currentPlayer) return null;

    const updatedPlayer = {
      ...this.currentPlayer,
      name: name,
      city: city
    };

    this.savePlayerToStorage(updatedPlayer);
    return updatedPlayer;
  }

  // Reset player (for testing)
  resetPlayer() {
    localStorage.removeItem('fokatmain_player');
    this.currentPlayer = null;
  }

  // Get random city for anonymous players
  getRandomCity() {
    const cities = [
      'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 
      'Kolkata', 'Pune', 'Ahmedabad', 'Surat', 'Jaipur',
      'Lucknow', 'Kanpur', 'Nagpur', 'Indore', 'Bhopal'
    ];
    return cities[Math.floor(Math.random() * cities.length)];
  }

  // Check if player has played a game today
  hasPlayedToday(gameId) {
    // This would typically check backend, for now return false
    return false;
  }

  // Generate security metadata for bot detection
  generateSecurityMetadata() {
    return {
      timestamp: Date.now(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      user_agent_hash: this.hashString(navigator.userAgent),
      has_touch: 'ontouchstart' in window,
      session_start: this.currentPlayer?.created_at || new Date().toISOString()
    };
  }

  // Simple hash function for fingerprinting
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }
}

// Create singleton instance
const playerManager = new PlayerManager();

export default playerManager;